import { useState } from 'react';
import type { PersonId } from '@shared/types.ts';
import { getStore, useAppState, useFeature, useIsOwner } from '../../data/store.ts';
import { FEATURE_KEYS, finishedKeys, stageOf } from '@shared/features.ts';
import { ScreenTitle } from '../../components/ui.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Link } from '../../lib/router.tsx';
import { config } from '../../config.ts';
import { enablePush, isStandalone, pushState } from '../../lib/push.ts';
import { LocalSource } from '../../data/localSource.ts';
import { checkForUpdate } from '../../lib/sw-register.ts';
import type { SupabaseSource } from '../../data/supabaseSource.ts';

/** "Oct 22, 3:41 PM" in this phone's time zone. */
function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "2 just you · 1 waiting for cleanup". */
function switchesSummary(features: Parameters<typeof stageOf>[0]): string {
  const count = (stage: string) => FEATURE_KEYS.filter((k) => stageOf(features, k) === stage).length;
  const ready = finishedKeys(features).length;
  const parts = [
    count('me') && `${count('me')} just you`,
    count('off') && `${count('off')} off`,
    count('everyone') && `${count('everyone')} everyone`,
    ready && `${ready} waiting for cleanup`,
  ].filter(Boolean);
  return parts.join(' · ') || 'None';
}

function Row({ to, title, detail }: { to: string; title: string; detail: string }) {
  return (
    <Link to={to} className="kv-row" style={{ color: 'var(--text)' }}>
      <span className="stack" style={{ gap: 2 }}>
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span className="small muted">{detail}</span>
      </span>
      <span className="muted">
        <Icon name="chevron-right" size={18} />
      </span>
    </Link>
  );
}

export function SettingsScreen() {
  const store = getStore();
  const me = useAppState((s) => s.me);
  const people = useAppState((s) => s.people);
  const stores = useAppState((s) => s.stores);
  const staples = useAppState((s) => s.staples);
  const settings = useAppState((s) => s.settings);
  const features = useAppState((s) => s.features);
  const groceries = useFeature('groceries');
  const language = useFeature('language');
  const laundry = useFeature('laundry');
  const owner = useIsOwner();
  const [pushMsg, setPushMsg] = useState('');
  const local = store.source.kind === 'local';
  const ps = pushState();

  const exportData = () => {
    const blob = new Blob([JSON.stringify(store.state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `natangelo-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="screen">
      <ScreenTitle title="Settings" />

      {laundry && (
        <div className="card-flush">
          <Row to="/settings/laundry" title="Laundry" detail="Washers, dryers, your balance" />
        </div>
      )}

      <section className="stack">
        <h2 className="section-label">You</h2>
        <div className="card stack-lg">
          {local ? (
            <>
              <span className="small muted">This device is used by</span>
              <div className="grid-2">
                {people.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`btn${me === p.id ? ' btn-soft' : ''}`}
                    aria-pressed={me === p.id}
                    onClick={() => store.setMe(p.id as PersonId)}
                  >
                    <span className={`avatar sm ${p.id}`}>{p.initial}</span>
                    {p.name}
                  </button>
                ))}
              </div>
              <span className="small muted">
                {config.isPreview ? 'Preview with sample data. ' : ''}Local mode: data stays on this device until Supabase is connected.
              </span>
            </>
          ) : (
            <div className="row-between">
              <span>
                Signed in as <strong>{people.find((p) => p.id === me)?.name ?? 'unknown'}</strong>
              </span>
              <button type="button" className="btn" onClick={async () => (await store.signOut(), location.reload())}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="stack">
        <h2 className="section-label">Notifications</h2>
        <div className="card stack">
          {ps === 'granted' && <span className="small">On for this device.</span>}
          {ps === 'needs-install' && (
            <span className="small muted">On iPhone, notifications need the app on your Home Screen: in Safari tap Share, then Add to Home Screen, and open it from there.</span>
          )}
          {ps === 'not-configured' && <span className="small muted">Not set up yet (needs the push key from setup).</span>}
          {ps === 'unsupported' && <span className="small muted">This browser doesn't support notifications.</span>}
          {ps === 'denied' && <span className="small muted">Blocked. Turn them on in iOS Settings → Notifications → Natangelo.</span>}
          {ps === 'default' && (
            <button
              type="button"
              className="btn btn-soft"
              disabled={local}
              onClick={async () => setPushMsg(await enablePush(store.source as SupabaseSource, me))}
            >
              <Icon name="bell" size={18} /> Turn on notifications
            </button>
          )}
          {local && ps === 'default' && <span className="small muted">Available once Supabase is connected.</span>}
          {pushMsg && <span className="small">{pushMsg}</span>}
          {!isStandalone() && ps !== 'needs-install' && <span className="tiny muted">Tip: install to the Home Screen for the full app experience.</span>}
        </div>
      </section>

      {groceries && (
        <section className="stack">
          <h2 className="section-label">Groceries</h2>
          <div className="card-flush">
            <Row to="/settings/staples" title="Staples and store matches" detail={`${staples.length} staples · ${staples.filter((s) => s.usual).length} in "the usual"`} />
            <Row to="/settings/stores" title="Stores and minimums" detail={stores.filter((s) => s.enabled).map((s) => s.name).join(', ')} />
            <div className="kv-row">
              <label htmlFor="quiet" className="k">
                Prepare carts after the list is quiet for
              </label>
              <select id="quiet" className="select" style={{ width: 110 }} value={settings.quietMinutes} onChange={(e) => store.updateSettings({ quietMinutes: Number(e.target.value) })}>
                {[5, 10, 15, 30, 60].map((m) => (
                  <option key={m} value={m}>
                    {m} min
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>
      )}

      {language && (
        <section className="stack">
          <h2 className="section-label">Language</h2>
          <div className="card-flush">
            <Row to="/language/settings" title="Voice and reminder" detail="Who reads your flashcards aloud, and a daily reminder" />
          </div>
        </section>
      )}

      <section className="stack">
        <h2 className="section-label">Travel</h2>
        <div className="card-flush">
          <Row to="/settings/people" title="People" detail={people.map((p) => p.name).join(' & ') + ' · names on bookings'} />
        </div>
        <div className="card stack">
          <span className="small muted">Forward bookings{groceries ? ' and store receipts' : ''} to</span>
          <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{settings.inboxAddress}</span>
          <span className="small muted">Booking emails show up in Travel within about a minute.{groceries ? ' Receipts teach the app prices.' : ''}</span>
        </div>
      </section>

      {owner && (
        <section className="stack">
          <h2 className="section-label">Developer</h2>
          <div className="card-flush">
            <Row to="/settings/developer" title="Feature switches" detail={switchesSummary(features)} />
          </div>
        </section>
      )}

      <section className="stack">
        <h2 className="section-label">Data</h2>
        <div className="card stack">
          <button type="button" className="btn" onClick={exportData}>
            Download a backup (JSON)
          </button>
          {local && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                if (!confirm('Reset this device to the sample data?')) return;
                (store.source as LocalSource).reset();
                void store.init();
              }}
            >
              Reset this device
            </button>
          )}
          <span className="tiny muted">
            Natangelo {config.version} · build {config.build.sha} · {formatBuildTime(config.build.time)}
          </span>
          <UpdateCheck />
        </div>
      </section>
    </div>
  );
}

/** Check for updates: finds a newer version and reloads into it, or says this is the newest. */
function UpdateCheck() {
  const [status, setStatus] = useState<'idle' | 'checking' | 'updating' | 'current' | 'unavailable'>('idle');
  const label = { idle: '', checking: 'Checking…', updating: 'Updating…', current: "You're on the newest version.", unavailable: 'Updates are checked in the installed app.' }[status];
  return (
    <div className="row" style={{ gap: 12, alignItems: 'center' }}>
      <button
        type="button"
        className="btn"
        disabled={status === 'checking' || status === 'updating'}
        onClick={async () => {
          setStatus('checking');
          setStatus(await checkForUpdate());
        }}
      >
        Check for updates
      </button>
      {label && <span className="tiny muted" role="status">{label}</span>}
    </div>
  );
}
